import { Component, HostListener, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { OnboardingStore } from '../onboarding.store';
import { SignupService } from '../../../tpiq-services/signup.service';
import { Session } from '../../../services/Session';
import { DynamicScriptLoaderService } from '../../../services/dynamic-script-loader.service';
import { MeetingSchedulerComponent } from './meeting-scheduler.component';
import { ToastService } from '../../../services/Toast.service';

@Component({
  selector: 'app-meeting-step',
  standalone: true,
  imports: [CommonModule, MeetingSchedulerComponent],
  templateUrl: './meeting-step.component.html',
})
export class MeetingStepComponent implements OnInit {
  // Set to true to use the custom API-based scheduler instead of the HubSpot embed
  useApiScheduler = true;

  hubspotEmbedUrl = '';
  showCalendar = false;
  meetingNotBooked = false; // kept for template compat — toast replaces inline error
  checkingMeeting = false;
  meetingBooked = false;

  org    = Session.getOrg();
  userId = Session.getUser().id;

  constructor(
    private store: OnboardingStore,
    private signupService: SignupService,
    private dynamicScriptLoaderService: DynamicScriptLoaderService,
    private toastService: ToastService,
  ) {}

  ngOnInit() {
    const user = Session.getUser();
    this.signupService.getMeetingDurations(this.org, this.userId).subscribe({
      next: (res) => {
        const slug = res.slug || 'taxplaniq-onboarding-team/onboarding';
        this.hubspotEmbedUrl = `https://meetings.hubspot.com/${slug}?embed=true`
          + `&firstname=${encodeURIComponent(user.first_name || '')}`
          + `&lastname=${encodeURIComponent(user.last_name || '')}`
          + `&email=${encodeURIComponent(user.id || '')}`;
        this.showCalendar = true;
        setTimeout(() => { this.dynamicScriptLoaderService.loadScript('hubspot'); });
      },
      error: () => {
        // Non-fatal — fall back to hardcoded slug so the embed still loads
        this.hubspotEmbedUrl = `https://meetings.hubspot.com/taxplaniq-onboarding-team/onboarding?embed=true`
          + `&firstname=${encodeURIComponent(user.first_name || '')}`
          + `&lastname=${encodeURIComponent(user.last_name || '')}`
          + `&email=${encodeURIComponent(user.id || '')}`;
        this.showCalendar = true;
        setTimeout(() => { this.dynamicScriptLoaderService.loadScript('hubspot'); });
      }
    });
  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent) {
    if (event.data?.meetingBookSucceeded) {
      this.meetingBooked = true;
      this.meetingNotBooked = false;
    }
  }

  // Called by the API-based scheduler when booking is confirmed
  onSchedulerBooked() {
    this.meetingBooked     = true;
    this.meetingNotBooked  = false;
  }

  skip() {
    this.meetingNotBooked = false;
    this.advance();
  }

  next() {
    this.meetingNotBooked = false;
    // HubSpot confirmed booking client-side — trust it, skip async webhook check
    if (this.meetingBooked) {
      this.advance();
      return;
    }
    this.checkingMeeting = true;
    this.signupService.checkForMeetingConfigured(this.org, this.userId).subscribe({
      next: () => {
        this.checkingMeeting = false;
        this.advance();
      },
      error: () => {
        this.checkingMeeting = false;
        this.toastService.pop('error', 'No meeting has been booked yet. Please book a meeting using the calendar above, or click "Skip for now" to continue.');
      }
    });
  }

  private advance() {
    // Meeting booking is asynchronous (set by HubSpot webhook) — no need to block
    const nextStatus = this.store.maxInvites() > 0 ? 'Invite' : 'Done';
    this.signupService.updateCompanyOnboarding(this.org, this.userId, nextStatus).subscribe({ error: () => {} });
    this.store.nextStep();
  }
}
